/**
 * Feb 2026 · /pay/return — Cashfree hosted-checkout return page.
 * The customer lands here after paying (or bailing) on Cashfree.
 * We poll the backend for the terminal payment state — the return
 * URL is never trusted as proof of payment; the webhook is the source
 * of truth so we keep polling until the backend confirms `success` or
 * `failed`.
 */
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";

const POLL_MS = 2500;
const MAX_POLLS = 20;   // ~50s of polling

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [state, setState] = useState({ status: "checking", message: "Verifying payment…" });
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!orderId) { setState({ status: "missing", message: "No order id — can't verify" }); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/public/studio/payment-status/${encodeURIComponent(orderId)}`);
        if (cancelled) return;
        setDetail(data);
        if (data.status === "success") {
          setState({ status: "success", message: "Payment successful — booking confirmed" });
          return;
        }
        if (data.status === "failed") {
          setState({ status: "failed", message: "Payment failed. Please try again from the confirmation email." });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({ status: "pending", message: "Payment is still processing. You'll receive an email once it's confirmed." });
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        if (attempts >= 3) {
          setState({ status: "unknown", message: "Unable to verify payment right now — please check your email." });
        } else {
          setTimeout(poll, POLL_MS);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [orderId]);

  const bg = "#0B2545";
  const accent = state.status === "success" ? "#10b981"
                : state.status === "failed" ? "#ef4444"
                : state.status === "pending" ? "#f59e0b" : "#EA5934";
  const icon = state.status === "success" ? "✓"
              : state.status === "failed" ? "✕"
              : state.status === "pending" ? "⏳" : "…";

  return (
    <div style={{ minHeight: "100vh", background: bg, color: "white", display: "grid",
                    placeItems: "center", fontFamily: "system-ui, -apple-system, sans-serif" }}
         data-testid="pay-return">
      <div style={{ maxWidth: 480, width: "calc(100% - 32px)", padding: 32,
                      background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)",
                      textAlign: "center" }}>
        <div style={{ fontSize: 56, color: accent, lineHeight: 1 }}>{icon}</div>
        <div style={{ marginTop: 12, fontSize: 10, letterSpacing: 3, textTransform: "uppercase",
                        opacity: 0.7 }}>
          Payment status
        </div>
        <h1 style={{ margin: "8px 0 0", fontSize: 22 }} data-testid="pay-return-status">{state.message}</h1>
        {detail?.booking_code && (
          <div style={{ marginTop: 16, fontSize: 13, opacity: 0.8 }}>
            Booking&nbsp;<b style={{ fontFamily: "monospace" }}>{detail.booking_code}</b>
            {detail.amount != null && <> · ₹{Number(detail.amount).toFixed(2)}</>}
          </div>
        )}
        {state.status === "checking" && (
          <div style={{ marginTop: 24, fontSize: 12, opacity: 0.6 }}>
            Verifying with Cashfree… don't close this window.
          </div>
        )}
        <div style={{ marginTop: 24 }}>
          <Link to="/" data-testid="pay-return-home"
                style={{ display: "inline-block", padding: "10px 20px", background: accent,
                            color: "white", textDecoration: "none", fontWeight: 700,
                            textTransform: "uppercase", letterSpacing: 2, fontSize: 11 }}>
            Return home
          </Link>
        </div>
      </div>
    </div>
  );
}
